/**
 * SCD — Règles de conseil RETAIL pour le moteur d'insights des conseillers
 * (Maurice le sceptique / Camille la bienveillante). Chaque règle lit
 * l'état `retail` réel : stock, marge, vitrine/leviers, fidélité,
 * satisfaction, promos, e-commerce.
 */
import { LEVIERS } from './retailMarketing';

const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

/* Couverture de stock : jours de vente restants au rythme des 7 derniers jours. */
const ruleStockCoverage = (s) => {
  const r = s.retail;
  if (!r) return null;
  const stockTotal = Object.values(r.stock || {}).reduce((a, x) => a + (x.qty || 0), 0);
  const ventes7 = (r.ventes7 || []).reduce((a, v) => a + (v.unites || v.ventes || 0), 0);
  const parJour = ventes7 / 7;
  if (parJour < 1) return null;
  const jours = Math.round(stockTotal / parJour);
  if (jours >= 5) return null;
  return {
    id: 'retail_stock_low',
    severity: jours <= 2 ? 'critical' : 'warning',
    category: 'commercial',
    title: `Stock tendu — ${jours} jour${jours > 1 ? 's' : ''} de couverture`,
    body: `Au rythme de vente actuel (${Math.round(parJour)} articles/jour), votre réserve sera vide dans ${jours} jour${jours > 1 ? 's' : ''}. Chaque rupture, c'est du chiffre d'affaires perdu ET de la satisfaction en moins. Passez commande dès aujourd'hui (pensez aux délais de livraison).`,
    actions: [{ label: 'Passer commande', to: '/achats' }],
    metric: `Couverture ${jours} j`,
    advisorTone: {
      maurice: `${jours} jours de stock. Un magasin aux rayons vides, c'est un magasin qui paie un loyer pour rien. Commandez, et vite.`,
      camille: `Votre stock file plus vite que prévu — bon signe pour la demande ! Anticipons : une commande aujourd'hui évite les rayons vides dans ${jours} jours.`,
    },
  };
};

/* Marge cumulée faible : prix trop bas ou promos trop généreuses. */
const ruleLowMargin = (s) => {
  const r = s.retail;
  if (!r || (r.stats?.caCumul || 0) < 5000) return null;
  const margePct = pct(r.stats?.margeCumul || 0, r.stats?.caCumul || 0);
  if (margePct >= 32) return null;
  return {
    id: 'retail_low_margin',
    severity: margePct < 22 ? 'critical' : 'warning',
    category: 'commercial',
    title: `Marge faible — ${margePct}% du CA`,
    body: `Votre marge cumulée est de ${margePct}% (repère sain : 35-50 % selon le secteur). Causes classiques : prix de vente trop bas vs prix conseillé, promotions trop longues, achats mal négociés. Vérifiez vos prix famille par famille.`,
    actions: [{ label: 'Revoir les prix', to: '/assortiment' }],
    metric: `Marge ${margePct}%`,
    advisorTone: {
      maurice: `${margePct}% de marge. Vous vendez, certes — mais vous travaillez presque gratuitement. Remontez vos prix ou arrêtez de brader.`,
      camille: `La marge est un peu courte (${margePct}%). Regardons ensemble : parfois +5 % sur deux familles peu sensibles au prix suffit à tout rééquilibrer.`,
    },
  };
};

/* Aucun levier marketing actif après 2 semaines de jeu. */
const ruleNoMarketing = (s) => {
  const r = s.retail;
  if (!r || (s.totalDays || 0) < 14) return null;
  const actifs = Object.values(r.marketing?.leviers || {}).filter(Boolean).length;
  if (actifs > 0) return null;
  return {
    id: 'retail_no_marketing',
    severity: 'opportunity',
    category: 'commercial',
    title: 'Aucun levier marketing actif',
    body: `Vitrine, communication locale, accueil, carte de fidélité… aucun des ${LEVIERS.length} leviers n'est activé. Quelques centaines d'euros par mois bien placés peuvent rapporter bien plus en trafic et en conversion.`,
    actions: [{ label: 'Ouvrir le Marketing', to: '/marketing' }],
    metric: `0/${LEVIERS.length} levier actif`,
    advisorTone: {
      maurice: `Zéro marketing. Vous attendez que les clients devinent votre existence ? Même la meilleure boutique du monde a besoin qu'on parle d'elle.`,
      camille: `Votre boutique mérite d'être connue ! Commencez petit : une belle vitrine et un peu de communication locale, et observez la différence sur le trafic.`,
    },
  };
};

/* Satisfaction clients basse : impacte les avis puis le trafic. */
const ruleLowSatisfaction = (s) => {
  const r = s.retail;
  const sat = r?.marketing?.satisfaction;
  if (sat == null || sat >= 55) return null;
  return {
    id: 'retail_low_satisfaction',
    severity: sat < 40 ? 'critical' : 'warning',
    category: 'commercial',
    title: `Satisfaction clients en berne — ${Math.round(sat)}/100`,
    body: `Les clients mécontents laissent de mauvais avis, et les mauvais avis font fuir les suivants (note actuelle : ${r.marketing?.noteAvis ?? '—'}★). Causes fréquentes : ruptures en rayon, attente en caisse (embauchez !), prix perçus trop élevés. Le levier « Accueil & service » aide aussi.`,
    actions: [
      { label: 'Voir le Marketing', to: '/marketing' },
      { label: "Renforcer l'équipe", to: '/equipe' },
    ],
    metric: `${Math.round(sat)}/100 · ${r.marketing?.noteAvis ?? '—'}★`,
    advisorTone: {
      maurice: `${Math.round(sat)}/100 de satisfaction. Vos clients votent avec leurs pieds — et leurs avis en ligne. Réglez les ruptures et la file en caisse avant de penser à autre chose.`,
      camille: `La satisfaction mérite un coup de pouce. Trois pistes douces : moins de ruptures, une caisse de plus aux heures de pointe, et le levier Accueil. Les avis remonteront tout seuls.`,
    },
  };
};

/* Habituation promo : promos trop fréquentes, effet émoussé. */
const rulePromoHabit = (s) => {
  const hab = s.retail?.marketing?.habituation || 0;
  if (hab < 0.4) return null;
  return {
    id: 'retail_promo_habit',
    severity: 'warning',
    category: 'commercial',
    title: 'Vos clients sont habitués aux promos',
    body: `À force de promotions, l'effet s'émousse : ${Math.round(hab * 100)}% de l'impact trafic est perdu par habitude, et votre marge trinque à chaque opération. Laissez respirer les prix quelques semaines — l'effet reviendra.`,
    actions: [{ label: 'Gérer les promotions', to: '/marketing' }],
    metric: `Habituation ${Math.round(hab * 100)}%`,
    advisorTone: {
      maurice: `Encore une promo ? Vos clients n'achètent plus jamais au prix normal — vous leur avez appris à attendre. Bravo. Coupez tout pendant un mois.`,
      camille: `Les promos, c'est comme le café : trop souvent, ça ne fait plus d'effet. Une pause de quelques semaines redonnera de la force à votre prochaine opération.`,
    },
  };
};

/* Fidélité non activée alors que la satisfaction est bonne. */
const ruleLoyaltyOpportunity = (s) => {
  const r = s.retail;
  if (!r || (s.totalDays || 0) < 30) return null;
  const sat = r.marketing?.satisfaction || 0;
  if (sat < 65 || r.marketing?.leviers?.fidelite) return null;
  return {
    id: 'retail_loyalty_opp',
    severity: 'opportunity',
    category: 'commercial',
    title: 'Vos clients sont contents — fidélisez-les !',
    body: `Satisfaction à ${Math.round(sat)}/100 : le moment idéal pour lancer la carte de fidélité. Un client fidèle revient plus souvent, dépense plus, et parle de vous. Le coût (points offerts ≈ 1 % du CA) est vite remboursé.`,
    actions: [{ label: 'Activer la fidélité', to: '/marketing' }],
    metric: `Satisfaction ${Math.round(sat)}/100`,
    advisorTone: {
      maurice: `Des clients satisfaits et pas de programme de fidélité ? Vous laissez vos concurrents récupérer VOS habitués. C'est de la négligence.`,
      camille: `Vos clients vous aiment déjà — offrez-leur une raison de plus de revenir ! La carte de fidélité transforme un bon passage en habitude.`,
    },
  };
};

/* E-commerce : trésorerie confortable et aucune présence en ligne. */
const ruleEcommerceOpportunity = (s) => {
  const r = s.retail;
  if (!r || (s.totalDays || 0) < 45) return null;
  if ((r.ecommerce?.palier || 0) > 0 || (s.treasury || 0) < 15000) return null;
  return {
    id: 'retail_ecom_opp',
    severity: 'opportunity',
    category: 'commercial',
    title: 'Aucune présence en ligne',
    body: `Avec ${Math.round((s.treasury || 0) / 1000)}k€ en caisse, un site vitrine (1 500 €) puis une boutique en ligne ouvrent un second canal de vente — et le click & collect ramène les clients web en magasin.`,
    actions: [{ label: "Ouvrir l'E-commerce", to: '/ecommerce' }],
    metric: 'Palier web : aucun',
    advisorTone: {
      maurice: `Pas de site en 2026. Vos clients vous cherchent sur leur téléphone et trouvent vos concurrents. Réveillez-vous.`,
      camille: `Le web n'est pas réservé aux géants ! Un simple site vitrine rassure déjà les clients, et le click & collect fait le pont avec votre boutique.`,
    },
  };
};

export const RETAIL_ADVISOR_RULES = [
  ruleStockCoverage,
  ruleLowMargin,
  ruleLowSatisfaction,
  rulePromoHabit,
  ruleNoMarketing,
  ruleLoyaltyOpportunity,
  ruleEcommerceOpportunity,
];
